import { Check, Copy, Plus, RefreshCw, Settings, Trash2 } from 'lucide-react'
import { Link } from 'react-router'

import { useConfigurations, useUpdateConfiguration } from 'query/useConfigurations'
import {
  Alert,
  Button,
  ButtonLink,
  ConfirmNameModal,
  CopyButton,
  DataTable,
  EmptyState,
  Figures,
  Icon,
  IconButton,
  LoadingIndicator,
  PageContent,
  PageHeader,
} from 'ui'

import CopyableSpan from './CopyableSpan'
import formatLicenseKey from './formatLicenseKey'

const DeleteConfirmModal = ({ id, name, onDeleteSuccess }) => {
  const { mutateAsync, isPending } = useUpdateConfiguration(id)

  return (
    <ConfirmNameModal
      actionLabel="Delete"
      confirmationAction="delete this configuration"
      confirmationButtonLabel="Delete"
      isPending={isPending}
      name={name}
      onConfirm={async () => {
        await mutateAsync({ status: 'trashed' })
        onDeleteSuccess()
      }}
      title="Delete Configuration"
      trigger={(
        <IconButton label={`Delete ${name}`} title="Delete Configuration" variant="danger">
          <Icon as={Trash2} />
        </IconButton>
      )}
    >
      Installed ExpanDrive clients will become unlicensed, and this configuration and its licenses cannot be restored.
    </ConfirmNameModal>
  )
}

export const DeploymentsIndex = () => {
  const { data: configurations, isError, errorMessage, invalidateQuery, isLoading } = useConfigurations()
  const activeUsers = configurations.reduce((total, configuration) => total + (configuration.users?.length || 0), 0)

  const columns = [
    {
      key: 'name',
      label: 'Configuration',
      primary: true,
      render: entry => (
        <div className="tw:grid tw:gap-1">
          <Link className="tw:font-semibold" to={`/configurations/${entry.id}`}>{entry.name}</Link>
          <span className="ds-helper">Drive and deployment settings</span>
        </div>
      ),
    },
    {
      key: 'key',
      label: 'License Key',
      span: true,
      render: entry => <CopyableSpan text={formatLicenseKey(entry.key)} />,
    },
    {
      key: 'users',
      label: 'Active Users',
      render: entry => (
        <span><strong className="tw:font-semibold tw:text-foreground-strong">{entry.users?.length || 0}</strong> <span className="tw:text-foreground-subtle">active</span></span>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        action={(
          <>
            <Button icon={<Icon as={RefreshCw} />} onClick={() => invalidateQuery()} variant="secondary">Refresh</Button>
            {configurations.length > 0 && <ButtonLink icon={<Icon as={Plus} />} to="/configurations/new">New Configuration</ButtonLink>}
          </>
        )}
        helpHref="https://docs.expandrive.com/web-management-console#configurations"
        helpLabel="Help — Configurations"
        subtitle="Manage drive configurations, licenses, and deployment settings for your team."
        title="Configurations"
      />
      <PageContent>
        {isLoading ? <LoadingIndicator /> : (
          <>
            <Figures
              className="tw:mb-10"
              items={[
                { value: configurations.length, label: 'Configurations' },
                { value: activeUsers, label: 'Active Users' },
              ]}
            />
            {isError && <Alert color="danger">{errorMessage || 'Unable to load configurations.'}</Alert>}
            <DataTable
              actions={entry => (
                <>
                  <CopyButton
                    copiedIcon={<Icon as={Check} />}
                    copyIcon={<Icon as={Copy} />}
                    copyLabel={`Copy License for ${entry.name}`}
                    text={formatLicenseKey(entry.key)}
                  />
                  <DeleteConfirmModal id={entry.id} name={entry.name} onDeleteSuccess={invalidateQuery} />
                </>
              )}
              columns={columns}
              empty={(
                <EmptyState
                  action={<ButtonLink icon={<Icon as={Plus} />} to="/configurations/new">New Configuration</ButtonLink>}
                  icon={<Icon as={Settings} size={32} />}
                  title="No Configurations Yet"
                >
                  Create one to share drive settings across your team.
                </EmptyState>
              )}
              rows={configurations}
            />
          </>
        )}
      </PageContent>
    </>
  )
}

export default DeploymentsIndex
